import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  Award, ShieldCheck, Building2, Trophy, GraduationCap, Sparkles,
  CheckCircle2, ArrowUpRight, ExternalLink, Globe2,
} from "lucide-react";

const AWARD_THEME = {
  verified:           { label: "Verified Institution",        icon: ShieldCheck, color: "from-ec-emerald to-emerald-700" },
  certified:          { label: "Certified Institution",       icon: Award,       color: "from-amber-500 to-amber-700" },
  top:                { label: "Top Institution",             icon: Trophy,      color: "from-violet-500 to-violet-800" },
  top_placement:      { label: "Top Placement Institution",   icon: Building2,   color: "from-sky-500 to-sky-800" },
  student_excellence: { label: "Student Excellence",          icon: GraduationCap, color: "from-rose-500 to-rose-800" },
  founder:            { label: "Founder Award",               icon: Sparkles,    color: "from-yellow-500 to-yellow-700" },
  innovation:         { label: "Innovation Award",            icon: Sparkles,    color: "from-pink-500 to-pink-700" },
  emerging:           { label: "Emerging Institution",        icon: Sparkles,    color: "from-teal-500 to-teal-700" },
  excellence:         { label: "Excellence Award",            icon: Sparkles,    color: "from-orange-500 to-orange-700" },
};
const STATUS_BADGE = {
  active:    "bg-ec-emerald/10 text-ec-emerald border-ec-emerald/40",
  expired:   "bg-orange-100 text-orange-700 border-orange-300",
  suspended: "bg-amber-100 text-amber-800 border-amber-300",
  revoked:   "bg-red-100 text-ec-error border-red-300",
};

export default function InstitutionAwardsPublic() {
  const { ethicId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/institutions/${ethicId}/awards`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Institution not found."));
  }, [ethicId]);

  if (err) return <div className="bg-ec-bg min-h-screen"><Navbar /><div className="max-w-3xl mx-auto px-6 py-24 text-center"><h1 className="font-display text-3xl font-black text-ec-error" data-testid="inst-not-found">{err}</h1></div><Footer /></div>;
  if (!data) return <div className="bg-ec-bg min-h-screen"><Navbar /><div className="max-w-3xl mx-auto px-6 py-24 text-center text-ec-ink-soft">Loading…</div><Footer /></div>;

  const inst = data.institution;
  const score = data.trust_score;
  const activeAwards = data.awards.filter((a) => a.status === "active");
  const history = data.awards;

  return (
    <div className="bg-ec-bg min-h-screen" data-testid="institution-award-page">
      <Navbar />
      <section className="border-b border-ec-line bg-white">
        <div className="max-w-5xl mx-auto px-6 py-12 lg:py-16 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-ec-line bg-ec-bg px-3 py-1 text-xs eyebrow">
              <Globe2 className="w-3 h-3 text-ec-emerald" /> Public Institution Profile
            </div>
            <div className="flex items-center gap-4 mt-5">
              {inst.avatar_url ? (
                <img src={inst.avatar_url} alt={inst.name} className="w-20 h-20 border border-ec-line rounded-sm object-cover" />
              ) : (
                <div className="w-20 h-20 bg-ec-ink text-white flex items-center justify-center"><Building2 className="w-9 h-9" /></div>
              )}
              <div>
                <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">{inst.name || "Institution"}</h1>
                <div className="font-mono-plex text-xs text-ec-ink-soft mt-1">{inst.ethic_id} · {inst.country || "—"}</div>
                {inst.verification_status && inst.verification_status !== "Unverified" && (
                  <Badge className="rounded-sm bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30 mt-2 gap-1"><CheckCircle2 className="w-3 h-3" /> {inst.verification_status}</Badge>
                )}
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <TrustScoreCard score={score} />
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-12">
        <h2 className="font-display text-2xl font-black mb-5">Active Recognition</h2>
        {activeAwards.length === 0 ? (
          <Card className="rounded-sm border-ec-line"><CardContent className="p-10 text-center text-ec-ink-soft" data-testid="active-awards-empty">No active awards.</CardContent></Card>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="active-awards-grid">
            {activeAwards.map((a) => <AwardCard key={a.id} a={a} />)}
          </div>
        )}

        <h2 className="font-display text-2xl font-black mt-12 mb-5">Recognition History</h2>
        <Card className="rounded-sm border-ec-line">
          <CardContent className="p-0">
            <ul className="divide-y divide-ec-line" data-testid="history-list">
              {history.length === 0 ? (
                <li className="p-8 text-center text-ec-ink-soft text-sm">No recognition history yet.</li>
              ) : history.map((a) => {
                const t = AWARD_THEME[a.award_type] || { label: a.award_label, icon: Award };
                const Icon = t.icon;
                return (
                  <li key={a.id} className="px-5 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`history-row-${a.id}`}>
                    <Icon className="w-5 h-5 text-ec-ink-soft" />
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-semibold">{t.label}</div>
                      <div className="font-mono-plex text-[10px] text-ec-ink-soft">#{a.award_no} · Issued {(a.issued_at || "").slice(0, 10) || "—"}{a.expires_at ? ` · Expires ${a.expires_at.slice(0, 10)}` : ""}</div>
                    </div>
                    <Badge className={`rounded-sm border ${STATUS_BADGE[a.status] || ""}`}>{a.status.replace("_", " ")}</Badge>
                    <Link to={`/verify/institution/${a.award_no}`} className="text-xs text-ec-emerald hover:underline inline-flex items-center gap-1">Verify <ExternalLink className="w-3 h-3" /></Link>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      </section>
      <Footer />
    </div>
  );
}

function TrustScoreCard({ score }) {
  if (!score) return null;
  const pct = score.total;
  return (
    <Card className="rounded-sm border-ec-emerald/30 bg-ec-emerald/5" data-testid="trust-score-card">
      <CardContent className="p-6">
        <div className="eyebrow text-ec-emerald mb-2">Institution Trust Score</div>
        <div className="flex items-end gap-3">
          <div className="font-display text-6xl font-black tracking-tighter">{pct}</div>
          <div className="text-ec-ink-soft font-mono-plex pb-2 text-sm">/ 100</div>
        </div>
        <div className="h-2 bg-white border border-ec-line mt-3 overflow-hidden">
          <div className="h-full bg-ec-emerald" style={{ width: `${pct}%` }} />
        </div>
        <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-ec-ink-soft font-mono-plex">
          {Object.entries(score.components || {}).map(([k, v]) => (
            <li key={k} className="flex justify-between">
              <span>{k.replace(/_/g, " ")}</span>
              <span className="text-ec-ink">{v} / {score.weights?.[k] || "—"}</span>
            </li>
          ))}
        </ul>
        <div className="text-[10px] text-ec-ink-soft mt-3">{score.student_count?.toLocaleString() || 0} student records on file</div>
      </CardContent>
    </Card>
  );
}

function AwardCard({ a }) {
  const t = AWARD_THEME[a.award_type] || { label: a.award_label, icon: Award, color: "from-zinc-500 to-zinc-800" };
  const Icon = t.icon;
  return (
    <Link to={`/verify/institution/${a.award_no}`} className="block" data-testid={`active-award-${a.id}`}>
      <Card className="rounded-sm border-ec-ink/10 hover:border-ec-emerald/40 transition-colors overflow-hidden">
        <CardContent className="p-0">
          <div className={`bg-gradient-to-br ${t.color} text-white p-6 noise-overlay`}>
            <Icon className="w-9 h-9 opacity-90" />
            <h3 className="font-display text-2xl font-black mt-2">{t.label}</h3>
            <div className="font-mono-plex text-[10px] opacity-80 mt-1">#{a.award_no}</div>
          </div>
          <div className="px-5 py-3 flex items-center justify-between bg-white">
            <div className="text-[11px] font-mono-plex text-ec-ink-soft">
              Issued {(a.issued_at || "").slice(0, 10)}{a.expires_at ? ` · Until ${a.expires_at.slice(0, 10)}` : " · Perpetual"}
            </div>
            <Badge className={`rounded-sm border ${STATUS_BADGE[a.status]}`}>{a.status}</Badge>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
